import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../components/ui/button';
import { SignInButton } from '@clerk/clerk-react';
import {
  Tv, Bell, ArrowRight, ChevronRight, Check,
  Zap, Mic, Chrome, Smartphone, Shield, Brain, Users, Dna, BarChart2, Trophy, Sparkles, Clock, User
} from 'lucide-react';

const SERVICES = ['Netflix', 'Hulu', 'Disney+', 'HBO Max', 'Prime Video', 'Apple TV+', 'Peacock', 'Paramount+'];

const FEATURES = [
  {
    icon: Mic, tag: 'AUTO-SYNC', title: 'Auto-Detect Shows',
    desc: 'Open Qude on your phone near the TV and it figures out what\'s playing — then adds it to your watchlist automatically. No searching, no typing.',
    gradient: 'linear-gradient(135deg, rgba(124,58,237,0.15) 0%, rgba(74,158,255,0.08) 100%)',
    border: 'rgba(124,58,237,0.35)', glow: '#7c3aed', accent: '#a78bfa',
  },
  {
    icon: Chrome, tag: 'NEW', title: 'Chrome Extension',
    desc: 'Install once and your watchlist stays up to date as you stream. Qude recognizes what you\'re watching and logs it automatically.',
    gradient: 'linear-gradient(135deg, rgba(74,158,255,0.12) 0%, rgba(59,130,246,0.06) 100%)',
    border: 'rgba(74,158,255,0.3)', glow: '#4A9EFF', accent: '#67e8f9',
  },
  {
    icon: Brain, tag: 'AI', title: 'MoodQ',
    desc: 'Tell Qude how you\'re feeling and it recommends the perfect show from across all your services.',
    gradient: 'linear-gradient(135deg, rgba(236,72,153,0.10) 0%, transparent 100%)',
    border: 'rgba(236,72,153,0.2)', glow: '#ec4899', accent: '#f9a8d4',
  },
  {
    icon: Bell, tag: 'SMART', title: 'Drop Alerts',
    desc: 'Get a push notification the moment a new episode drops for any show on your watchlist.',
    gradient: 'linear-gradient(135deg, rgba(245,158,11,0.10) 0%, transparent 100%)',
    border: 'rgba(245,158,11,0.2)', glow: '#f59e0b', accent: '#fcd34d',
  },
  {
    icon: Shield, tag: 'UNIQUE', title: 'Spoiler Shield',
    desc: 'Qude tracks where you are in a show and warns you when you\'re behind — before social media gives anything away.',
    gradient: 'linear-gradient(135deg, rgba(16,185,129,0.10) 0%, transparent 100%)',
    border: 'rgba(16,185,129,0.2)', glow: '#10b981', accent: '#6ee7b7',
  },
  {
    icon: Dna, tag: 'INSIGHTS', title: 'Watch DNA',
    desc: 'See your viewing personality, top service, finish rate, and personal stats — all in one place.',
    gradient: 'linear-gradient(135deg, rgba(99,102,241,0.10) 0%, transparent 100%)',
    border: 'rgba(99,102,241,0.2)', glow: '#6366f1', accent: '#a5b4fc',
  },
  {
    icon: Tv, tag: 'CORE', title: 'All Services, One Hub',
    desc: 'Netflix, Hulu, Disney+, HBO Max, Prime Video, Apple TV+, Peacock, Paramount+ — one tap to play.',
    gradient: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 100%)',
    border: 'rgba(255,255,255,0.08)', glow: '#ffffff', accent: '#e5e7eb',
  },
  {
    icon: Users, tag: 'SOCIAL', title: 'QMates',
    desc: 'Watch together with friends. Sync watchlists, see who\'s ahead, and share queues with zero spoilers.',
    gradient: 'linear-gradient(135deg, rgba(20,184,166,0.10) 0%, transparent 100%)',
    border: 'rgba(20,184,166,0.2)', glow: '#14b8a6', accent: '#5eead4',
  },
  {
    icon: BarChart2, tag: 'CROWD DATA', title: 'Finish Rate Score',
    desc: 'See what percentage of Qude users actually finish a show before you commit to watching it. Only on Qude.',
    gradient: 'linear-gradient(135deg, rgba(251,146,60,0.10) 0%, transparent 100%)',
    border: 'rgba(251,146,60,0.2)', glow: '#fb923c', accent: '#fdba74',
  },
  {
    icon: Trophy,
    tag: 'SCORES',
    title: 'Live Sports Scores',
    desc: 'Follow your teams across NFL, NBA, MLB, NHL, soccer and more. Live scores personalized to you — right in your streaming hub.',
    gradient: 'linear-gradient(135deg, rgba(250,204,21,0.10) 0%, transparent 100%)',
    border: 'rgba(250,204,21,0.2)',
    glow: '#facc15',
    accent: '#fde047',
  },
];

const TIERS = [
  {
    name: 'Basic', price: 3.99,
    features: ['Connect up to 4 streaming services', 'Full watchlist management', 'Unlimited genre folders', 'Drop Alerts', 'Chrome extension'],
    highlight: false,
  },
  {
    name: 'Standard', price: 5.99,
    features: ['Connect up to 6 streaming services', 'Everything in Basic', 'MoodQ AI recommendations', 'Spoiler Shield', 'Watch DNA insights', 'Up to 5 QMates'],
    highlight: true,
  },
  {
    name: 'Premium', price: 9.99,
    features: ['Unlimited streaming services', 'Everything in Standard', 'Auto-detect shows', 'Unlimited QMates', 'Priority support', 'Early access features'],
    highlight: false,
  },
];

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" xmlns="http://www.w3.org/2000/svg">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
}

function PlayStoreIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.18 23.76c.3.17.64.24.99.2l.11-.03L13.65 14 10 10.35 3.18 23.76zM20.46 10.37l-2.92-1.67-4 3.9 4 3.9 2.95-1.69c.84-.48.84-1.96-.03-2.44zM2.01 1.05C1.96 1.22 1.94 1.4 1.94 1.6v20.8c0 .2.02.38.07.55L12 13 2.01 1.05zM13.65 10l3.49-3.4-9.37-5.4c-.32-.18-.65-.26-.98-.24L13.65 10z"/>
    </svg>
  );
}

function StarField() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = document.documentElement.scrollHeight;
      drawStars();
    };
    const drawStars = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const count = Math.floor((canvas.width * canvas.height) / 8000);
      for (let i = 0; i < count; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const r = Math.random() * 1.2 + 0.2;
        const opacity = Math.random() * 0.6 + 0.1;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${opacity})`;
        ctx.fill();
      }
      for (let i = 0; i < 12; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const r = Math.random() * 1.5 + 0.8;
        const grad = ctx.createRadialGradient(x, y, 0, x, y, r * 4);
        grad.addColorStop(0, 'rgba(180,180,255,0.8)');
        grad.addColorStop(1, 'rgba(180,180,255,0)');
        ctx.beginPath();
        ctx.arc(x, y, r * 4, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      }
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  return (
    <canvas ref={canvasRef}
      style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }} />
  );
}

// ── Intent-detection demo ────────────────────────────────────────────────────
// Illustrative, client-side only -- this is NOT a live call to Qude's real
// recommendation engine (that needs an actual signed-in account's viewing
// history to say anything; see backend/server.py's _compute_taste_weights).
// It exists to make one specific claim concrete for a visitor who has never
// used the product: Qude separates "what do you normally like" from "what do
// you want RIGHT NOW," and combines them -- rather than just asking a
// visitor to take that on faith. The mood set below intentionally mirrors
// the real MOOD_GENRE_MAP in backend/server.py (a subset of it, sized for a
// demo) rather than inventing a different taxonomy for marketing purposes.
const DEMO_MOODS = [
  { id: 'stressed', label: 'Stressed', emoji: '😤', genres: ['comedy'] },
  { id: 'happy', label: 'Happy', emoji: '😄', genres: ['comedy', 'romance'] },
  { id: 'sad', label: 'Sad', emoji: '😢', genres: ['drama', 'romance'] },
  { id: 'adventurous', label: 'Adventurous', emoji: '🤠', genres: ['action', 'scifi'] },
  { id: 'energetic', label: 'Energetic', emoji: '⚡', genres: ['action', 'scifi', 'crime'] },
  { id: 'contemplative', label: 'Thoughtful', emoji: '🤔', genres: ['drama', 'mystery'] },
];

const DEMO_TIMES = [
  { id: 'short', label: '~20 min', maxRuntime: 30 },
  { id: 'medium', label: '~45 min', maxRuntime: 60 },
  { id: 'long', label: '2+ hours', maxRuntime: 999 },
];

const DEMO_TITLES = [
  { title: 'It’s Always Sunny in Philadelphia', service: 'Hulu', genres: ['comedy'], runtime: 22, type: 'series', intensity: 'light', discovery: false },
  { title: 'Abbott Elementary', service: 'Hulu', genres: ['comedy'], runtime: 22, type: 'series', intensity: 'light', discovery: true },
  { title: 'Nobody Wants This', service: 'Netflix', genres: ['romance', 'comedy'], runtime: 30, type: 'series', intensity: 'light', discovery: true },
  { title: 'Ted Lasso', service: 'Apple TV+', genres: ['comedy', 'drama'], runtime: 30, type: 'series', intensity: 'light', discovery: false },
  { title: 'This Is Us', service: 'Hulu', genres: ['drama', 'romance'], runtime: 45, type: 'series', intensity: 'medium', discovery: false },
  { title: 'Normal People', service: 'Hulu', genres: ['drama', 'romance'], runtime: 28, type: 'series', intensity: 'medium', discovery: true },
  { title: 'The Night Agent', service: 'Netflix', genres: ['action', 'crime'], runtime: 50, type: 'series', intensity: 'heavy', discovery: true },
  { title: 'Breaking Bad', service: 'Netflix', genres: ['crime', 'drama'], runtime: 47, type: 'series', intensity: 'heavy', discovery: false },
  { title: 'Severance', service: 'Apple TV+', genres: ['scifi', 'mystery'], runtime: 55, type: 'series', intensity: 'heavy', discovery: false },
  { title: 'True Detective', service: 'HBO Max', genres: ['crime', 'mystery'], runtime: 55, type: 'series', intensity: 'heavy', discovery: false },
  { title: 'Glass Onion', service: 'Netflix', genres: ['mystery', 'comedy'], runtime: 139, type: 'movie', intensity: 'medium', discovery: true },
  { title: 'Palm Springs', service: 'Hulu', genres: ['comedy', 'romance'], runtime: 90, type: 'movie', intensity: 'light', discovery: true },
  { title: 'Everything Everywhere All at Once', service: 'Showtime', genres: ['scifi', 'action'], runtime: 140, type: 'movie', intensity: 'medium', discovery: true },
  { title: 'Rick and Morty', service: 'Hulu', genres: ['comedy', 'scifi'], runtime: 22, type: 'series', intensity: 'medium', discovery: false },
];

// Pure scoring function, same shape as the real backend's philosophy (see
// server.py's _compute_taste_weights docstring: weighted, additive signals,
// never a black box) -- simplified to what a demo with no real account can
// actually know: the four context inputs the visitor just set, nothing else.
function scoreDemoTitle(title, { mood, time, alone, discovery }) {
  let score = 0;
  const reasons = [];
  const moodGenres = DEMO_MOODS.find((m) => m.id === mood)?.genres || [];
  if (title.genres.some((g) => moodGenres.includes(g))) {
    score += 3;
    reasons.push(`fits ${DEMO_MOODS.find((m) => m.id === mood).label.toLowerCase()}`);
  }
  const timeConf = DEMO_TIMES.find((t) => t.id === time);
  if (title.runtime <= timeConf.maxRuntime) {
    score += 2;
    reasons.push(time === 'short' ? 'a quick watch' : time === 'medium' ? 'fits your time tonight' : 'worth the long sit');
  }
  if (!alone && title.intensity === 'heavy') score -= 2;
  if (alone && title.intensity === 'heavy') { score += 1; reasons.push('nothing held back'); }
  if (!alone && title.intensity === 'light') { score += 1; reasons.push('easy to watch together'); }
  if (discovery === title.discovery) { score += 2; reasons.push(discovery ? 'something new' : 'a familiar favorite'); }
  return { title, score, reasons };
}

function IntentDemo() {
  const [mood, setMood] = useState('stressed');
  const [time, setTime] = useState('medium');
  const [alone, setAlone] = useState(true);
  const [discovery, setDiscovery] = useState(false);
  const [revealKey, setRevealKey] = useState(0);

  const context = { mood, time, alone, discovery };

  const ranked = useMemo(() => {
    return DEMO_TITLES
      .map((t) => scoreDemoTitle(t, context))
      .sort((a, b) => b.score - a.score);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mood, time, alone, discovery]);

  const pick = ranked[0];

  // Replays the reveal animation on every context change, not just mount --
  // the whole point is to visibly show the recommendation CHANGING as
  // context changes, not just land on a static answer.
  useEffect(() => {
    setRevealKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mood, time, alone, discovery]);

  const setOpt = (setter) => (val) => setter(val);

  return (
    <div className="relative rounded-3xl overflow-hidden border p-6 sm:p-10"
      style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.08), rgba(74,158,255,0.04))', borderColor: 'rgba(124,58,237,0.2)' }}>
      <div className="grid lg:grid-cols-[1fr_340px] gap-10 items-start">
        {/* Context controls */}
        <div>
          <p className="text-xs font-black tracking-widest text-violet-400 uppercase mb-2">Try it — no account needed</p>
          <h3 className="text-2xl sm:text-3xl font-black mb-6" style={{ fontFamily: 'Outfit' }}>
            What do you want to watch <span className="text-violet-400">right now?</span>
          </h3>

          <p className="text-xs font-bold tracking-widest text-neutral-500 uppercase mb-2">Mood</p>
          <div className="flex flex-wrap gap-2 mb-6">
            {DEMO_MOODS.map((m) => (
              <button key={m.id} onClick={() => setOpt(setMood)(m.id)}
                className={`px-3 py-2 rounded-full text-sm font-semibold transition-all border ${mood === m.id ? 'border-violet-500 bg-violet-600/20 text-white' : 'border-white/10 text-neutral-400 hover:border-white/25'}`}>
                {m.emoji} {m.label}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-xs font-bold tracking-widest text-neutral-500 uppercase mb-2 flex items-center gap-1.5">
                <Clock className="w-3 h-3" /> Time available
              </p>
              <div className="flex gap-2">
                {DEMO_TIMES.map((t) => (
                  <button key={t.id} onClick={() => setOpt(setTime)(t.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${time === t.id ? 'border-violet-500 bg-violet-600/20 text-white' : 'border-white/10 text-neutral-400 hover:border-white/25'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold tracking-widest text-neutral-500 uppercase mb-2 flex items-center gap-1.5">
                <User className="w-3 h-3" /> Watching
              </p>
              <div className="flex gap-2">
                <button onClick={() => setOpt(setAlone)(true)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${alone ? 'border-violet-500 bg-violet-600/20 text-white' : 'border-white/10 text-neutral-400 hover:border-white/25'}`}>
                  Alone
                </button>
                <button onClick={() => setOpt(setAlone)(false)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${!alone ? 'border-violet-500 bg-violet-600/20 text-white' : 'border-white/10 text-neutral-400 hover:border-white/25'}`}>
                  With someone
                </button>
              </div>
            </div>
          </div>

          <p className="text-xs font-bold tracking-widest text-neutral-500 uppercase mb-2">Familiarity</p>
          <div className="flex gap-2">
            <button onClick={() => setOpt(setDiscovery)(false)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${!discovery ? 'border-violet-500 bg-violet-600/20 text-white' : 'border-white/10 text-neutral-400 hover:border-white/25'}`}>
              A familiar favorite
            </button>
            <button onClick={() => setOpt(setDiscovery)(true)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${discovery ? 'border-violet-500 bg-violet-600/20 text-white' : 'border-white/10 text-neutral-400 hover:border-white/25'}`}>
              Something new
            </button>
          </div>
        </div>

        {/* Recommendation card */}
        <div>
          <div key={revealKey} className="rounded-2xl p-5 border" style={{ background: 'rgba(0,0,0,0.35)', borderColor: 'rgba(255,255,255,0.08)', animation: 'qudeDemoReveal 0.35s ease-out' }}>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-violet-400" />
              <span className="text-xs font-bold tracking-widest text-violet-300 uppercase">Qude'd Up</span>
            </div>
            <p className="text-xs text-neutral-500 mb-1">{pick.title.service} · {pick.title.type === 'movie' ? `${pick.title.runtime} min film` : `${pick.title.runtime} min episode`}</p>
            <h4 className="text-2xl font-black mb-3" style={{ fontFamily: 'Outfit' }}>{pick.title.title}</h4>
            <p className="text-sm text-neutral-400 leading-relaxed">
              {pick.reasons.length > 0
                ? `Because it ${pick.reasons.join(', ')}.`
                : 'The closest match from this demo set for that combination.'}
            </p>
          </div>
          <p className="text-xs text-neutral-600 mt-4 leading-relaxed">
            Illustrative demo using a small example set — not a live connection to your account. The real Qude does this against your actual watch history and every title on your services.
          </p>
        </div>
      </div>
      <style>{`@keyframes qudeDemoReveal { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading || !user) return;
    // ProtectedRoute stashes the page a signed-out visit was headed to
    // (a shared show link, a QMate invite, a push-notification target)
    // as location.state.from before bouncing here to sign in. Without
    // reading it back, every one of those deep links silently dumped the
    // user at generic Hub after login instead of what they clicked.
    const from = location.state?.from;
    const dest = from ? `${from.pathname || '/hub'}${from.search || ''}` : '/hub';
    navigate(dest, { replace: true });
  }, [user, loading, navigate, location.state]);

  return (
    <div className="min-h-screen text-white overflow-x-hidden" style={{ background: '#050508' }}>
      <StarField />

      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 border-b border-white/5"
        style={{ background: 'rgba(5,5,8,0.85)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between"
          style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))', paddingBottom: '1rem' }}>
          <img src="/logo.png" alt="Qude" className="w-16 h-16 object-contain" />
          <div className="flex items-center gap-3">
            <a href="#features" className="text-sm text-neutral-400 hover:text-white transition-colors hidden sm:block">Features</a>
            <a href="#pricing" className="text-sm text-neutral-400 hover:text-white transition-colors hidden sm:block">Pricing</a>
            <SignInButton mode="modal" redirectUrl="/hub">
              <button className="text-sm font-semibold text-neutral-300 hover:text-white transition-colors px-4 py-2 rounded-full border border-white/10 hover:border-white/20">
                Sign In
              </button>
            </SignInButton>
            <SignInButton mode="modal" redirectUrl="/hub">
              <Button className="bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white font-semibold rounded-full px-6 hover:opacity-90 transition-all">
                Get Started
              </Button>
            </SignInButton>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-36 pb-24 px-6 overflow-hidden" style={{ zIndex: 1 }}>
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(124,58,237,0.12) 0%, transparent 70%)', filter: 'blur(40px)' }} />
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm text-violet-300 mb-8 font-medium"
            style={{ background: 'rgba(124,58,237,0.1)', borderColor: 'rgba(124,58,237,0.25)' }}>
            <Zap className="w-4 h-4" />
            Your streaming command center
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight mb-6 leading-[1.05]" style={{ fontFamily: 'Outfit' }}>
            One place for
            <span className="block bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
              all your shows
            </span>
          </h1>
          <p className="text-lg text-neutral-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Stop switching between eight streaming apps. Qude unifies your watchlist, logs what you watch automatically, and tells you exactly what to watch tonight.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <SignInButton mode="modal" redirectUrl="/hub">
              <Button className="bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white font-bold rounded-full px-8 py-6 text-base hover:opacity-90 transition-all group"
                style={{ boxShadow: '0 0 40px rgba(124,58,237,0.3)' }}>
                Get Started
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </SignInButton>
            <SignInButton mode="modal" redirectUrl="/hub">
              <button className="flex items-center gap-2 text-sm font-semibold text-neutral-300 hover:text-white transition-colors py-3 px-6 rounded-full border border-white/10 hover:border-white/20">
                Sign In
              </button>
            </SignInButton>
            <a href="#demo" className="flex items-center gap-2 text-sm text-neutral-500 hover:text-white transition-colors py-3">
              See how it works <ChevronRight className="w-4 h-4" />
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {SERVICES.map(name => (
              <span key={name} className="text-xs font-semibold tracking-widest uppercase text-neutral-600">{name}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Auto-sync hero card */}
      <section className="py-8 px-6" style={{ position: 'relative', zIndex: 1 }}>
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden border p-8 sm:p-12"
            style={{ background: 'linear-gradient(135deg, #150828 0%, #080820 50%, #081828 100%)', borderColor: 'rgba(124,58,237,0.25)', boxShadow: '0 0 80px rgba(124,58,237,0.1)' }}>
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(ellipse, rgba(124,58,237,0.12) 0%, transparent 70%)', filter: 'blur(40px)' }} />
            <div className="relative flex flex-col lg:flex-row items-start lg:items-center gap-8">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs text-violet-300 font-bold tracking-widest uppercase mb-4"
                  style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)' }}>
                  <Mic className="w-3 h-3" /> Works on Any Screen
                </div>
                <h2 className="text-3xl sm:text-4xl font-black mb-4 leading-tight" style={{ fontFamily: 'Outfit' }}>
                  Your watchlist,<br />always up to date
                </h2>
                <p className="text-neutral-400 leading-relaxed mb-6 max-w-lg">
                  Open Qude on your phone near the TV and it figures out what's playing — then adds it to your watchlist. No searching, no typing, no effort.
                </p>
                <div className="flex flex-wrap gap-3">
                  {['Any TV or screen', 'Skips ads automatically', 'Zero manual entry', 'Syncs across devices'].map(t => (
                    <div key={t} className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs text-neutral-300"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <Check className="w-3 h-3 text-emerald-400" /> {t}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0 w-full lg:w-64 rounded-2xl p-4 space-y-3"
                style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-xs text-neutral-400 font-medium">Recognizing...</span>
                </div>
                <div className="h-px" style={{ background: 'rgba(255,255,255,0.05)' }} />
                {[
                  { show: 'Breaking Bad', conf: '99%', time: '2 min ago' },
                  { show: 'The Bear', conf: '97%', time: '94 min ago' },
                  { show: 'Succession', conf: '95%', time: 'Yesterday' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-white">{item.show}</p>
                      <p className="text-xs text-neutral-600">{item.time}</p>
                    </div>
                    <span className="text-xs font-bold text-emerald-400">{item.conf}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intent-detection demo */}
      <section id="demo" className="py-8 px-6" style={{ position: 'relative', zIndex: 1 }}>
        <div className="max-w-5xl mx-auto">
          <IntentDemo />
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 px-6" style={{ position: 'relative', zIndex: 1 }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-black tracking-widest text-violet-400 uppercase mb-3">Features</p>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Everything you need.<br />Nothing you don't.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map(({ icon: Icon, tag, title, desc, gradient, border, glow, accent }) => (
              <div key={title}
                className="group rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 relative overflow-hidden"
                style={{ background: gradient, border: `1px solid ${border}`, minHeight: 220 }}>
                <div className="absolute top-0 right-0 w-32 h-32 rounded-full pointer-events-none opacity-30"
                  style={{ background: `radial-gradient(ellipse, ${glow}40 0%, transparent 70%)`, filter: 'blur(16px)' }} />
                <div className="relative">
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ background: `${glow}20`, border: `1px solid ${glow}35` }}>
                      <Icon className="w-5 h-5" style={{ color: accent }} />
                    </div>
                    <span className="text-xs font-black tracking-widest px-2.5 py-1 rounded-full"
                      style={{ color: accent, background: `${glow}12`, border: `1px solid ${glow}25` }}>
                      {tag}
                    </span>
                  </div>
                  <h3 className="text-base font-black mb-2" style={{ fontFamily: 'Outfit', color: '#fff' }}>{title}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App availability */}
      <section className="py-16 px-6" style={{ position: 'relative', zIndex: 1 }}>
        <div className="max-w-4xl mx-auto">
          <div className="rounded-3xl border p-8 sm:p-12 flex flex-col sm:flex-row items-center gap-8"
            style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.06)' }}>
            <div className="flex-1">
              <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-3">Available on</p>
              <h2 className="text-2xl sm:text-3xl font-black mb-4" style={{ fontFamily: 'Outfit' }}>Take Qude everywhere</h2>
              <p className="text-sm text-neutral-400 leading-relaxed mb-6">
                Web app, Android app, or Chrome extension — Qude follows you across every screen.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="https://play.google.com/store/apps/details?id=com.qudetv.app" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors hover:bg-white/8"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <Smartphone className="w-4 h-4 text-emerald-400" /> Android App
                </a>
                <a href="/extension-token"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors hover:bg-white/8"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <Chrome className="w-4 h-4 text-[#4A9EFF]" /> Chrome Extension
                </a>
                <SignInButton mode="modal" redirectUrl="/hub">
                  <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors hover:bg-white/8"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <Tv className="w-4 h-4 text-violet-400" /> Web App
                  </button>
                </SignInButton>
              </div>
            </div>
            <div className="flex-shrink-0 w-40 h-40 rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.15), rgba(74,158,255,0.15))', border: '1px solid rgba(124,58,237,0.2)' }}>
              <Tv className="w-16 h-16" style={{ color: 'rgba(124,58,237,0.5)' }} />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6" style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-black tracking-widest text-[#4A9EFF] uppercase mb-3">Pricing</p>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>Simple, transparent pricing</h2>
            <p className="text-neutral-500 mt-3 text-sm">Start free. Upgrade when you're ready. Cancel anytime.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map(({ name, price, features, highlight }) => (
              <div key={name} className="relative p-8 rounded-2xl border transition-all"
                style={{
                  background: highlight ? 'linear-gradient(135deg, #150828 0%, #0a0a1a 100%)' : 'rgba(255,255,255,0.02)',
                  borderColor: highlight ? 'rgba(124,58,237,0.4)' : 'rgba(255,255,255,0.06)',
                  boxShadow: highlight ? '0 0 50px rgba(124,58,237,0.15)' : 'none',
                }}>
                {highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold whitespace-nowrap text-white"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)' }}>
                    Most Popular
                  </div>
                )}
                <h3 className="text-lg font-black mb-1" style={{ fontFamily: 'Outfit' }}>{name}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-black">${price}</span>
                  <span className="text-sm text-neutral-500">/mo</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {features.map(f => (
                    <li key={f} className="flex items-start gap-3 text-sm text-neutral-300">
                      <Check className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <SignInButton mode="modal" redirectUrl="/hub">
                  <Button className={`w-full rounded-full font-bold ${highlight ? 'text-white hover:opacity-90' : 'text-white hover:bg-white/10'}`}
                    style={highlight ? { background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)' } : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    Get Started
                  </Button>
                </SignInButton>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-neutral-600 mt-8">Free tier available · No credit card required to start</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6" style={{ position: 'relative', zIndex: 1 }}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="relative rounded-3xl overflow-hidden border p-12"
            style={{ background: 'linear-gradient(135deg, #150828 0%, #0a1828 100%)', borderColor: 'rgba(124,58,237,0.25)', boxShadow: '0 0 80px rgba(124,58,237,0.1)' }}>
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(124,58,237,0.15) 0%, transparent 60%)' }} />
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-black mb-4" style={{ fontFamily: 'Outfit' }}>Ready to watch smarter?</h2>
              <p className="text-neutral-400 mb-8 max-w-md mx-auto">Join thousands of people who never lose track of what they're watching.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <SignInButton mode="modal" redirectUrl="/hub">
                  <Button className="text-white font-bold rounded-full px-10 py-6 text-base hover:opacity-90 transition-all group"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)', boxShadow: '0 0 40px rgba(124,58,237,0.4)' }}>
                    Start for Free
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </SignInButton>
                <SignInButton mode="modal" redirectUrl="/hub">
                  <button className="text-sm font-semibold text-neutral-400 hover:text-white transition-colors py-3 px-6 rounded-full border border-white/10 hover:border-white/20">
                    Already have an account? Sign In
                  </button>
                </SignInButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download section */}
      <section className="py-16 px-6" style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-4">Download the App</p>
          <h2 className="text-2xl font-black mb-2" style={{ fontFamily: 'Outfit' }}>Qude on every device</h2>
          <p className="text-sm text-neutral-500 mb-8">Web, mobile, and browser extension — always in sync.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <div className="relative flex items-center gap-3 px-6 py-4 rounded-2xl opacity-50 cursor-not-allowed"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', minWidth: 190 }}>
              <AppleIcon />
              <div className="text-left">
                <p className="text-xs text-neutral-500 leading-none mb-0.5">Download on the</p>
                <p className="text-sm font-bold text-white leading-none">App Store</p>
              </div>
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0"
                style={{ background: 'rgba(124,58,237,0.2)', color: '#a78bfa', border: '1px solid rgba(124,58,237,0.3)' }}>
                Soon
              </span>
            </div>
            <a href="https://play.google.com/store/apps/details?id=com.qudetv.app"
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 px-6 py-4 rounded-2xl transition-all hover:-translate-y-0.5 hover:border-white/20"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', minWidth: 190 }}>
              <PlayStoreIcon />
              <div className="text-left">
                <p className="text-xs text-neutral-500 leading-none mb-0.5">Get it on</p>
                <p className="text-sm font-bold text-white leading-none">Google Play</p>
              </div>
            </a>
            <a href="/extension-token"
              className="flex items-center gap-3 px-6 py-4 rounded-2xl transition-all hover:-translate-y-0.5 hover:border-white/20"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', minWidth: 190 }}>
              <Chrome className="w-5 h-5 text-[#4A9EFF]" />
              <div className="text-left">
                <p className="text-xs text-neutral-500 leading-none mb-0.5">Install for</p>
                <p className="text-sm font-bold text-white leading-none">Chrome</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6" style={{ borderTop: '1px solid rgba(255,255,255,0.05)', position: 'relative', zIndex: 1 }}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="Qude" className="w-12 h-12 object-contain" />
          <div className="flex items-center gap-6 text-xs text-neutral-600">
            <a href="#features" className="hover:text-neutral-400 transition-colors">Features</a>
            <a href="#pricing" className="hover:text-neutral-400 transition-colors">Pricing</a>
            <a href="mailto:support@qudetv.com" className="hover:text-neutral-400 transition-colors">Support</a>
            <SignInButton mode="modal" redirectUrl="/hub">
              <button className="hover:text-neutral-400 transition-colors">Sign In</button>
            </SignInButton>
          </div>
          <p className="text-xs text-neutral-700">© 2026 Qude. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
